/**
 * Utility functions for audio notifications
 */

// Create a singleton audio context
let audioContext = null;

/**
 * Initialize the audio context (must be called from a user action)
 */
export const initAudioContext = () => {
  if (!audioContext) {
    try {
      const AudioContext = window.AudioContext || window.webkitAudioContext;
      audioContext = new AudioContext();
    } catch (e) {
      console.error('Web Audio API not supported:', e);
    }
  }
  return audioContext;
};

/**
 * Play a notification sound
 * @param {Object} options - Options for the notification sound
 * @param {number} options.frequency - Frequency of the sound (default: 830)
 * @param {number} options.duration - Duration in milliseconds (default: 200)
 * @param {number} options.volume - Volume from 0 to 1 (default: 0.5)
 * @param {string} options.type - Oscillator type (default: 'sine')
 */
export const playNotificationSound = (options = {}) => {
  try {
    const ctx = initAudioContext();
    if (!ctx) return;
    
    // Resume audio context if it's suspended (browser policy)
    if (ctx.state === 'suspended') {
      ctx.resume();
    }
    
    const frequency = options.frequency || 830;
    const duration = options.duration || 200;
    const volume = options.volume || 0.5; // Increased default volume from 0.2 to 0.5
    const type = options.type || 'sine';
    
    // Create oscillator and gain node
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    const compressor = ctx.createDynamicsCompressor(); // Add compressor for louder sound
    
    // Configure oscillator
    oscillator.type = type;
    oscillator.frequency.value = frequency;
    
    // Configure gain (volume)
    gainNode.gain.value = volume;
    
    // Configure compressor for louder sound without distortion
    compressor.threshold.value = -50;
    compressor.knee.value = 40;
    compressor.ratio.value = 12;
    compressor.attack.value = 0;
    compressor.release.value = 0.25;
    
    // Connect nodes with compressor
    oscillator.connect(gainNode);
    gainNode.connect(compressor);
    compressor.connect(ctx.destination);
    
    // Start and stop the sound
    oscillator.start();
    setTimeout(() => {
      oscillator.stop();
    }, duration);
  } catch (error) {
    console.error('Error playing notification sound:', error);
  }
};

/**
 * Play a double notification sound (for new notifications)
 */
export const playNewNotificationSound = () => {
  // First tone - higher volume (0.4 instead of 0.15)
  playNotificationSound({ frequency: 830, duration: 120, volume: 0.4, type: 'triangle' });
  
  // Second tone - higher volume and slightly longer duration
  setTimeout(() => {
    playNotificationSound({ frequency: 1046.5, duration: 180, volume: 0.4, type: 'triangle' });
  }, 150);
};
